/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Checkbox,
  Grid,
  LinearProgress,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import type { Coordinates } from '@opengeoweb/shared';
import {
  AlertBanner,
  CustomAccordion,
  SearchField,
  ToolContainerDraggable,
} from '@opengeoweb/shared';
import type { LocationsResponseFromBE } from '../../api/edr-api/types';
import { useSoundingsTranslation } from '../../utils/i18n';

const RowComponent = ({
  index,
  style,
  service,
  isChecked,
  updateSelectedLocation,
}: RowComponentProps<{
  service: ServiceWithLocations;
  isChecked: (sourceId: string, locationId: string) => boolean;
  updateSelectedLocation: (
    serviceId: string,
    locationId: string | undefined,
  ) => void;
}>): React.ReactElement => {
  const location = service.features[index];
  return (
    <div key={`${service.id}-${location.id}`} style={style}>
      <ListItem disableGutters disablePadding>
        <ListItemButton
          dense
          onClick={() => {
            updateSelectedLocation(
              service.id,
              isChecked(service.id, location.id) ? undefined : location.id,
            );
          }}
        >
          <ListItemIcon>
            <Checkbox
              checked={isChecked(service.id, location.id)}
              disableRipple
              edge="start"
              slotProps={{
                input: { 'aria-labelledby': location.id },
              }}
              tabIndex={-1}
            />
          </ListItemIcon>
          <ListItemText id={location.id} primary={location.properties.name} />
        </ListItemButton>
      </ListItem>
    </div>
  );
};

export type ServiceWithLocations = LocationsResponseFromBE & {
  id: string;
  name: string;
};
interface Props {
  services: ServiceWithLocations[];
  selectedSourcesAmount: number;
  selectedLocations: Record<string, string | Coordinates | undefined>;
  isOpen: boolean;
  onClose: () => void;
  updateSelectedLocation: (
    sourceId: string,
    locationId: string | undefined,
  ) => void;
  onMouseDown?: () => void;
  order?: number;
  isLoading?: boolean;
  error?: Error | null;
}
export const LocationsMenu = ({
  services,
  selectedSourcesAmount,
  selectedLocations,
  isOpen,
  onClose,
  updateSelectedLocation,
  onMouseDown = (): void => {},
  order = 0,
  isLoading,
  error,
}: Props): React.ReactElement => {
  const [searchFilter, setSearchFilter] = useState('');
  const { t } = useSoundingsTranslation();

  const filteredResults = useMemo(() => {
    return services?.map((service) => {
      const filteredFeatures = service.features
        ? service.features.filter((feature) => {
            return feature.properties?.name
              .toLowerCase()
              .includes(searchFilter.toLowerCase());
          })
        : [];

      return {
        ...service,
        features: filteredFeatures,
      };
    });
  }, [services, searchFilter]);

  const isChecked = (sourceId: string, locationId: string): boolean =>
    selectedLocations[sourceId] === locationId;

  return (
    <ToolContainerDraggable
      bounds="parent"
      isOpen={isOpen}
      minWidth={264}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      startPosition={{ top: 60, left: 200 }}
      startSize={{ width: 320, height: 500 }}
      title={t('locations')}
    >
      {!!isLoading && (
        <LinearProgress sx={{ width: '100%', position: 'absolute' }} />
      )}
      {!!error && <AlertBanner shouldClose title={error.message} />}
      {selectedSourcesAmount === 0 ? (
        <Typography sx={{ margin: 1 }}>
          {t('locations-menu-warning')}
        </Typography>
      ) : (
        <Grid container>
          <Box
            sx={{
              width: '100%',
              padding: 1,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'geowebColors.background.surfaceApp',
            }}
          >
            <SearchField
              searchFilter={searchFilter}
              setSearchFilter={setSearchFilter}
              small
            />
          </Box>
          {filteredResults.map((service) => (
            <Fragment key={service.id}>
              <CustomAccordion
                amountOfItems={service.features.length}
                defaultExpanded={false}
                sx={{
                  width: '100%',
                  margin: '0 8px 2px 8px',
                  '.MuiAccordionDetails-root': {
                    padding: 0,
                  },
                  '.MuiAccordionSummary-root': {
                    minHeight: '40px',
                  },
                  '.MuiAccordionSummary-content': {
                    paddingLeft: 3,
                  },
                }}
                title={service.name}
              >
                <List
                  rowComponent={RowComponent}
                  rowCount={service.features.length}
                  rowHeight={40}
                  rowProps={{
                    service,
                    isChecked,
                    updateSelectedLocation,
                  }}
                  style={{ height: 320, width: '100%' }}
                />
              </CustomAccordion>
            </Fragment>
          ))}
        </Grid>
      )}
    </ToolContainerDraggable>
  );
};
